import React, { useState, useEffect } from "react";
import cityService from "../../services/cityService";
import countryService from "../../services/countryService";
import AppTable from "../../components/ui/AppTable";
import ListToolbar from "../../components/ui/ListToolbar";

const CityList = ({ refreshTrigger }) => {
  const [cities, setCities] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCountry, setSelectedCountry] = useState("");

  useEffect(() => {
    loadData();
  }, [refreshTrigger]);

  useEffect(() => {
    filterCitiesByCountry();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCountry]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [citiesResponse, countriesResponse] = await Promise.all([
        cityService.getAllCities(),
        countryService.getAllCountries(),
      ]);

      if (citiesResponse.success) {
        setCities(citiesResponse.data || []);
      }
      if (countriesResponse.success) {
        setCountries(countriesResponse.data || []);
      }
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  const filterCitiesByCountry = async () => {
    if (selectedCountry) {
      try {
        const response = await cityService.getCitiesByCountry(selectedCountry);
        if (response.success) {
          setCities(response.data || []);
        }
      } catch (error) {
        console.error("Error filtering cities:", error);
      }
    } else {
      // Reload all cities
      const response = await cityService.getAllCities();
      if (response.success) {
        setCities(response.data || []);
      }
    }
  };

  const getCountryName = (countryId) => {
    const country = countries.find((c) => c.id === countryId);
    return country ? country.name : "Unknown";
  };

  return (
    <div className="city-list">
      <ListToolbar
        title="Cities"
        subtitle={`${cities.length} cit${cities.length !== 1 ? "ies" : "y"} found${selectedCountry ? ` in ${countries.find((c) => c.id.toString() === selectedCountry)?.name}` : ""}`}
      >
        <div className="row g-3">
          <div className="col-md-6">
            <label className="form-label fw-medium">Filter by Country</label>
            <select
              className="form-select"
              value={selectedCountry}
              onChange={(e) => setSelectedCountry(e.target.value)}
            >
              <option value="">All Countries</option>
              {countries.map((country) => (
                <option key={country.id} value={country.id}>
                  {country.name}
                </option>
              ))}
            </select>
          </div>
          {selectedCountry && (
            <div className="col-md-6 d-flex align-items-end">
              <button
                className="btn btn-outline-secondary"
                onClick={() => setSelectedCountry("")}
              >
                <i className="fas fa-times me-2"></i>
                Clear Filter
              </button>
            </div>
          )}
        </div>
      </ListToolbar>

      {/* Cities Table */}
      <div className="card border-0 shadow-sm">
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="text-muted mt-2">Loading cities...</p>
            </div>
          ) : cities.length === 0 ? (
            <div className="text-center py-5">
              <i
                className="fas fa-city text-muted mb-3"
                style={{ fontSize: "3rem" }}
              ></i>
              <h5 className="text-muted">No Cities Found</h5>
              <p className="text-muted">
                {selectedCountry
                  ? "No cities found for the selected country."
                  : "No cities have been added yet."}
              </p>
            </div>
          ) : (
            <AppTable responsive>
                <thead>
                  <tr>
                    <th className="ps-4">City</th>
                    <th>Country</th>
                    <th className="text-center pe-4">City ID</th>
                  </tr>
                </thead>
                <tbody>
                  {cities.map((city) => (
                    <tr key={city.id} className="border-bottom">
                      <td className="ps-4">
                        <div className="d-flex align-items-center">
                          <div
                            className="city-icon rounded-circle bg-info bg-opacity-10 d-flex align-items-center justify-content-center me-3"
                            style={{ width: "45px", height: "45px" }}
                          >
                            <i className="fas fa-building text-info"></i>
                          </div>
                          <div>
                            <div className="fw-bold text-dark">{city.name}</div>
                            <small className="text-muted">ID: {city.id}</small>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="badge bg-success bg-opacity-10 text-success">
                          <i className="fas fa-flag me-1"></i>
                          {getCountryName(city.countryId)}
                        </span>
                      </td>
                      <td className="text-center pe-4">
                        <span className="badge bg-primary">{city.id}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
            </AppTable>
          )}
        </div>
      </div>
    </div>
  );
};

export default CityList;
